// src/components/ChatPage.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDynamicChatHeight } from '../../hooks/useDynamicChatHeight';
import { Box, CircularProgress } from '@mui/material';
import { RootState, AppDispatch } from '../../redux/store';
import {
  sendMessage,
  fetchConversationsMetadata,
  fetchConversationMessagesThunk,
  startListeningForConversations,
  clearChat,
  addMessage,
  setCurrentConversation,
  updateConversationState,
  addConversation,
  deleteConversationThunk,
} from '../../redux/slices/chat';
import { refreshToken, getValidToken } from '../../redux/slices/userSlice';
import { handleTokenError } from '../../utils/tokenErrorHandler';
import { Message, ChatSession, Conversation, MessageAttachment } from '../../models/Message';
import { checkAuthSession } from '../../redux/slices/userSlice';
import { v4 as uuidv4 } from 'uuid';
import styles from './ChatPage.module.scss';
import { extractURLsFromText } from '../../types/documentUpload';
import { markDocumentsAsSent, markDocumentsAsDeleted } from '../../services/documentUploadService';
import { doc, onSnapshot, collection } from 'firebase/firestore';
import { db } from '../../services/firebaseConfig';
import { 
  listenToConversation, 
  listenToConversationMessages, 
  listenToConversationState
} from '../../services/conversationService';
import { ThinkingMode, ConversationType } from '../../redux/slices/chat/chatTypes';
import { getAvailableModes, getDefaultMode, MODE_CONFIG, isLegacyConversationType } from '../../redux/slices/chat/chatConfig';
import ChatInput from '../../components/chat/ChatInput';
import History from '../../components/chat/History';
import ChatHeader from './ChatHeader';
import ChatMessages from './ChatMessages';

/**
 * FEATURE FLAG: URL Detection
 * 
 * Status: DISABLED - Waiting for backend URL scraping implementation
 * 
 * @see ChatInput.tsx for corresponding flag
 */
const ENABLE_URL_DETECTION = false;

const ChatPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { 
    messages, 
    loading, 
    loadingHistory, 
    loadingMessages, 
    currentConversationId,
    conversations,
    pagination
  } = useSelector(
    (state: RootState) => state.chat
  );
  const { user, isAuthenticated, loading: userLoading } = useSelector((state: RootState) => state.user);
  const currentUser = useSelector((state: RootState) => state.user.user);
  const [currentThinkingMode, setCurrentThinkingMode] = useState<ThinkingMode>('reflection');
  const [messageInput, setMessageInput] = useState('');
  const [selectedSession, setSelectedSession] = useState<Conversation | null>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const [isHistoryOpenMobile, setIsHistoryOpenMobile] = useState(false);
  // Check if this is truly a first message (new conversation) vs just messages not loaded yet
  const isFirstMessage = messages.length === 0 && !selectedSession;
  const [currentConversationBusy, setCurrentConversationBusy] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [appInitialized, setAppInitialized] = useState(false);
  const [lastListenerTimestamp, setLastListenerTimestamp] = useState<string | undefined>(undefined);
  
  // Message ID state for attachments - pre-generate for document uploads
  const [currentMessageId, setCurrentMessageId] = useState<string>(uuidv4());
  // Attachments state
  const [attachments, setAttachments] = useState<MessageAttachment[]>([]);
  // Raw File objects for multipart upload (new conversation with documents)
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  
  // Grab the conversation states mapping from Redux
  const conversationStates = useSelector((state: RootState) => state.chat.conversationStates);

  const conversationState = currentConversationId
    ? conversationStates[currentConversationId] // safe to index here if currentConversationId is defined
    : undefined;

  const finalConversationState = conversationState || {
    status: 'idle',
    progress: '',
  };

  // Now use finalConversationState for status
  const conversationStatus = finalConversationState.status;

  // Add a new ref to track when we're explicitly loading messages
  const explicitLoadingRef = useRef(false);

  useDynamicChatHeight();

  // Redirect if not authenticated - but wait for auth loading to complete
  useEffect(() => {
    if (!userLoading && !isAuthenticated) {
      console.log('🔄 User not authenticated after loading completed, redirecting to signin');
      navigate('/signin');
    }
  }, [isAuthenticated, userLoading, navigate]);

  // Set up conversation listeners when the user is authenticated - improved to prevent multiple setups
  useEffect(() => {
    const initializeConversations = async () => {
      // Wait for auth loading to complete before attempting any token operations
      if (userLoading || !user?.id || !isAuthenticated || appInitialized) {
        console.log('🔄 Skipping conversation initialization:', { userLoading, userId: user?.id, isAuthenticated, appInitialized });
        return;
      }
      
      console.log('🚀 Starting conversation initialization for authenticated user');
      
      try {
        // Get a fresh token for the initial setup
        const validToken = await dispatch(getValidToken()).unwrap();
        
        // Initial fetch of conversations to populate the list
        await dispatch(fetchConversationsMetadata(validToken));
        
        // Mark app as initialized after initial data load
        setAppInitialized(true);
        
        // Start listening for real-time updates
        dispatch(startListeningForConversations({
          userToken: validToken,
          callback: (conversations: Conversation[]) => {
            // If we have a current conversation selected, check if its status changed
            if (currentConversationId) {
              const updatedConversation = conversations.find((c: Conversation) => c.id === currentConversationId);
              if (updatedConversation) {
                // Update local state to reflect conversation status
                setCurrentConversationBusy(updatedConversation.loading);
                setProgress(updatedConversation.progress || '');
              }
            }
          }
        }));
      } catch (error) {
        const errorMessage = await handleTokenError(error, {
          operation: 'conversation initialization',
          operationType: 'conversation-load',
          enableFallback: true,
          showUserNotification: true,
          dispatch: dispatch,
          logError: true
        });
        console.error('Failed to initialize conversations:', errorMessage);
        // TODO: Add user notification system for error feedback
      }
    };

    initializeConversations();
  }, [dispatch, user?.id, isAuthenticated, userLoading, appInitialized, currentConversationId]);

  // Restart listener when pagination changes to keep it synchronized
  useEffect(() => {
    const restartListener = async () => {
      // Only restart if app is initialized and we have pagination state
      if (!appInitialized || !user?.id || !isAuthenticated || !pagination.lastTimestamp) return;
      
      // Avoid unnecessary restarts if timestamp hasn't changed
      if (lastListenerTimestamp === pagination.lastTimestamp) return;

      try {
        console.log('Pagination changed, restarting listener with updated timestamp:', pagination.lastTimestamp);
        setLastListenerTimestamp(pagination.lastTimestamp);
        
        // Get a fresh token for the listener restart
        const validToken = await dispatch(getValidToken()).unwrap();
        
        // Restart the listener with the updated pagination timestamp
        dispatch(startListeningForConversations({
          userToken: validToken,
          callback: (conversations: Conversation[]) => {
            // If we have a current conversation selected, check if its status changed
            if (currentConversationId) {
              const updatedConversation = conversations.find((c: Conversation) => c.id === currentConversationId);
              if (updatedConversation) {
                // Update local state to reflect conversation status
                setCurrentConversationBusy(updatedConversation.loading);
                setProgress(updatedConversation.progress || '');
              }
            }
          }
        }));
      } catch (error) {
        const errorMessage = await handleTokenError(error, {
          operation: 'conversation listener restart',
          operationType: 'conversation-load',
          enableFallback: true,
          showUserNotification: true,
          dispatch: dispatch,
          logError: true
        });
        console.error('Failed to restart conversation listener:', errorMessage);
        // TODO: Add user notification system for error feedback
      }
    };

    restartListener();
  }, [dispatch, user?.id, isAuthenticated, appInitialized, pagination.lastTimestamp, currentConversationId, lastListenerTimestamp]);

  // Clear chat state only on component unmount
  useEffect(() => {
    return () => {
      dispatch(clearChat(false));
    };
  }, [dispatch]);

  // Set up listeners for the current conversation's messages - improved to avoid race conditions
  useEffect(() => {
    // Only set up listeners if:
    // 1. We have a valid conversation ID
    // 2. The user is authenticated
    // 3. We're not in the middle of an explicit load
    if (!currentConversationId || !user?.id || explicitLoadingRef.current) return;
    
    const userId = user.id;
    
    // Find current conversation from state to get thinkingType
    const currentConversation = conversations.find(c => c.id === currentConversationId);
    if (!currentConversation) return;
    
    // Get the conversation type, if empty or false default to reflection
    const conversationType = currentConversation.thinkingType || 'reflection';
    
    // Listen to messages for this conversation
    const unsubscribe = listenToConversationMessages(
      userId,
      currentConversationId,
      conversationType,
      (messagesFromFirebase) => {
        // When messages change, update the state
        if (messagesFromFirebase.length > 0) {
          // Clear existing messages and add the new ones
          dispatch(clearChat(true)); // Keep conversation ID
          
          // Add each message to state - ensure we clone the full message with all properties
          messagesFromFirebase.forEach(message => {
            try {
              // Make a full deep clone of the message to preserve all properties
              const messageClone = JSON.parse(JSON.stringify(message));
              
              // Enhance message with necessary metadata
              if (messageClone.thinkingType === 'fraktal') {
                // Ensure extra_parameters exists
                if (!messageClone.extra_parameters) {
                  messageClone.extra_parameters = {};
                }
                
                // Determine if this is a bot response in a Sein-II conversation (always true now)
                const isBotResponse = messageClone.id.includes('_response') && messageClone.sender === 'bot';
                
                // Set Sein-II parameters for all fraktal bot responses
                if (isBotResponse) {
                  messageClone.extra_parameters.sein_chain_version = 'SEIN_2';
                  messageClone.extra_parameters.fraktal_complexity = 'sein_complexity_2';
                }
              }
              
              // Enhance prompt-graph messages with proper metadata
              if (messageClone.thinkingType === 'prompt-graph') {
                // Ensure extra_parameters exists
                if (!messageClone.extra_parameters) {
                  messageClone.extra_parameters = {};
                }
              }
              
              dispatch(addMessage(messageClone));
            } catch (e) {
              console.error(`Error processing message ${message.id}:`, e);
              dispatch(addMessage(message));
            }
          });
        }
      }
    );
    
    return () => {
      unsubscribe();
    };
  }, [currentConversationId, user?.id, dispatch, conversations]);

  // Set up listeners for the current conversation's state - improved for better stability
  useEffect(() => {
    if (!currentConversationId || !user?.id) return;
    
    const userId = user.id;
    
    // Find current conversation from state to get thinkingType
    const currentConversation = conversations.find(c => c.id === currentConversationId);
    if (!currentConversation) return;
    
    // Get the conversation type, if empty or false default to reflection
    const conversationType = currentConversation.thinkingType || 'reflection';
    
    // Listen to conversation state
    const unsubscribe = listenToConversationState(
      userId,
      currentConversationId,
      conversationType,
      (state) => {
        // Preserve existing processingType from Redux to prevent race condition
        // where optimistic UI update gets overwritten by Firebase listener
        const currentState = conversationStates[currentConversationId];
        const existingProcessingType = currentState?.processingType;
        
        // Update conversation state in Redux
        dispatch(
          updateConversationState({
            conversationId: currentConversationId,
            status: state.status,
            progress: state.progress,
            type: state.type,
            // Preserve processingType when busy, clear when idle
            processingType: state.status === 'busy' ? existingProcessingType : undefined
          })
        );
        
        // Update local state
        setProgress(state.progress);
        setCurrentConversationBusy(state.status === 'busy');
      }
    );
    
    return () => {
      unsubscribe();
    };
  }, [currentConversationId, user?.id, dispatch, conversations, conversationStates]);

  // Helper function to ensure thinking mode is valid for conversation context
  const ensureValidThinkingMode = useCallback(() => {
    // If we have no current conversation ID, do nothing
    if (!currentConversationId) return;
    
    // Find the current conversation from redux state
    const currentConversation = conversations.find(c => c.id === currentConversationId);
    if (!currentConversation) return;
    
    // Get available modes for this conversation
    const availableModes = getAvailableModes(currentConversation.thinkingType, isFirstMessage);
    
    // If current mode is not available, switch to default mode
    if (!availableModes.includes(currentThinkingMode)) {
      const defaultMode = getDefaultMode(currentConversation.thinkingType, isFirstMessage);
      setCurrentThinkingMode(defaultMode);
    }
  }, [currentConversationId, conversations, currentThinkingMode, isFirstMessage]);

  // Update handleSendMessage function to use thinking mode
  const handleSendMessage = async (customParams?: { project_id?: string; version_id?: string }) => {
    if (!currentConversationId) return; // safeguard

    // Block sending to legacy conversations
    const currentConversation = conversations.find(c => c.id === currentConversationId);
    if (isLegacyConversationType(currentConversation?.thinkingType)) {
      console.warn('Attempted to send message to legacy conversation:', currentConversationId);
      return; // Block the send
    }

    // Ensure the current thinking mode is valid for this conversation
    ensureValidThinkingMode();
    
    // Calculate the effective thinking mode for this message
    // Auto-switch to fast-thinking for follow-up messages in prompt-graph conversations
    const effectiveThinkingMode: ThinkingMode = 
      (!isFirstMessage && currentThinkingMode === 'prompt-graph') 
        ? 'fast-thinking' 
        : currentThinkingMode;
    
    // Update state for UI consistency (async, affects next render)
    if (effectiveThinkingMode !== currentThinkingMode) {
      setCurrentThinkingMode(effectiveThinkingMode);
    }

    // Retrieve the conversation-specific status
    const conversationStatus = conversationStates[currentConversationId]?.status || 'idle';

    if (messageInput.trim() && currentUser && conversationStatus === 'idle') {
      try {
        // Get a valid token - will only refresh if needed
        const token = await dispatch(getValidToken()).unwrap();
        
        // Use pre-generated messageId
        const messageId = currentMessageId;
        
        // Extract URLs from message text and create URL attachments (only if feature is enabled)
        const detectedURLs = ENABLE_URL_DETECTION ? extractURLsFromText(messageInput) : [];
        const urlAttachments: MessageAttachment[] = detectedURLs.map(url => ({
          id: uuidv4(),
          type: 'url' as const,
          name: url,
          url: url,
          status: 'done' as const,
          metadata: {} // Backend will enrich this
        }));
        
        // Combine document attachments and URL attachments
        const allAttachments = [...attachments, ...urlAttachments];
        
        const newMessage: Message = {
          id: messageId,
          sender: 'user',
          text: messageInput,
          createdAt: new Date().toISOString(),
          conversationId: currentConversationId,
          thinkingType: 'user',
          attachments: allAttachments.length > 0 ? allAttachments : undefined
        };

        setMessageInput('');
        
        // Mark all document attachments as sent in Firestore
        const documentAttachments = allAttachments.filter(a => a.type === 'document');
        if (documentAttachments.length > 0 && currentUser?.id) {
          const documentIds = documentAttachments.map(a => a.id);
          try {
            await markDocumentsAsSent(currentUser.id, documentIds);
            console.log(`[ChatPage] Marked ${documentIds.length} documents as sent`);
          } catch (error) {
            console.error('[ChatPage] Failed to mark documents as sent:', error);
            // Don't block message sending if this fails
          }
        }
        
        setAttachments([]); // Clear attachments after sending
        setPendingFiles([]); // Clear pending files
        
        // Generate new message ID for next message
        const nextMessageId = uuidv4();
        setCurrentMessageId(nextMessageId);
        console.log(`[ChatPage] Generated new message ID for next message: ${nextMessageId}`);
        
        // Get conversation type from the actual conversation data
        const currentConversation = conversations.find(c => c.id === currentConversationId);
        const conversationType = currentConversation?.thinkingType || 
          (isFirstMessage ? effectiveThinkingMode as 'reflection' | 'fraktal' | 'prompt-graph' : 'reflection');
        
        // Mark the conversation as busy in Redux immediately
        dispatch(
          updateConversationState({
            conversationId: currentConversationId,
            status: 'busy',
            progress: '',
            type: conversationType,
            processingType: effectiveThinkingMode, // Use the calculated effective thinking mode
          })
        );
        
        // Update local UI state to reflect busy status
        setCurrentConversationBusy(true);

        // Send the message with the validated token
        try {
          // Build extra parameters based on thinking mode and custom params
          let extra_parameters: any = undefined;
          
          if (effectiveThinkingMode === 'fraktal') {
            extra_parameters = {
              fraktal_complexity: 'sein_complexity_2'
            };
          } else if (effectiveThinkingMode === 'prompt-graph' && customParams) {
            extra_parameters = {
              ...customParams
            };
          }
          
          await dispatch(
            sendMessage({
              message: newMessage,
              inputText: messageInput,
              thinkingMode: effectiveThinkingMode,
              isFirstMessage: isFirstMessage,
              extra_parameters,
              files: isFirstMessage && pendingFiles.length > 0 ? pendingFiles : undefined,
            })
          );
          // Message sent successfully
        } catch (error) {
          console.error('Failed to send message:', error);
          
          // Reset conversation status on error
          dispatch(
            updateConversationState({
              conversationId: currentConversationId,
              status: 'idle',
              progress: '',
              type: conversationType,
            })
          );
          setCurrentConversationBusy(false);
        }
      } catch (error) {
        const errorMessage = await handleTokenError(error, {
          operation: 'message sending',
          operationType: 'chat-message',
          enableFallback: true,
          showUserNotification: true,
          dispatch: dispatch,
          logError: true,
          metadata: { messageInput, thinkingMode: effectiveThinkingMode, isFirstMessage, conversationId: currentConversationId }
        });
        console.error('Error getting valid token for message sending:', errorMessage);
        setCurrentConversationBusy(false);
      }
    }
  };

  const handleSelectSession = async (session: Conversation) => {
    try {
      // Set the flag to prevent listeners from interfering
      explicitLoadingRef.current = true;
      
      setSelectedSession(session);
      setMessageInput('');
      
      // Clear attachments and mark as deleted in Firestore
      await clearAttachmentsAndMarkDeleted();
      
      dispatch(clearChat(false));
  
      // Log and set the current conversation
      dispatch(setCurrentConversation(session.id));
  
      // Check the conversation loading status directly from the Conversation object
      const isBusy = session.loading;
  
      // Update progress information
      const progress = session.progress || '';
  
      // Dispatch to update conversationStates in Redux, so the UI sees the correct status.
      dispatch(
        updateConversationState({
          conversationId: session.id,
          status: isBusy ? 'busy' : 'idle',
          progress,
          type: session.thinkingType, // Use the session's thinkingType
        })
      );
  
      // Set the appropriate thinking mode based on the conversation's context
      // For existing conversations, we should never treat them as "first message"
      // since they already exist in the database with a conversation type
      const isNewConversation = false; // This is an existing conversation being selected
      const defaultMode = getDefaultMode(session.thinkingType, isNewConversation);
      setCurrentThinkingMode(defaultMode);
  
      // Fetch messages for the selected conversation
      if (user?.id && isAuthenticated) {
        try {
          // Get a fresh token for message fetching
          const validToken = await dispatch(getValidToken()).unwrap();
          
          await dispatch(fetchConversationMessagesThunk({
            userToken: validToken,
            conversationId: session.id,
            type: session.thinkingType
          })).unwrap();
        } catch (error) {
          console.error('Error loading messages:', error);
          // Could add user notification here in future
        } finally {
          // Clear the flag after a short delay to ensure state has stabilized
          setTimeout(() => {
            explicitLoadingRef.current = false;
          }, 500);
        }
      }
    } catch (error) {
      console.error('Error selecting conversation:', error);
      // Clean up flag on error
      explicitLoadingRef.current = false;
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const toggleHistoryMobile = () => {
    setIsHistoryOpenMobile(!isHistoryOpenMobile);
  };

  /**
   * Helper function to clear attachments and mark documents as deleted in Firestore
   * This ensures proper cleanup when switching conversations or starting new chats
   */
  const clearAttachmentsAndMarkDeleted = useCallback(async () => {
    // Get current attachments - check if there are any to clear
    if (attachments.length === 0) {
      return; // Nothing to clear
    }
    
    // Filter to get only document attachments (not URLs)
    const documentAttachments = attachments.filter(a => a.type === 'document');
    
    // Mark documents as deleted in Firestore if there are any
    if (documentAttachments.length > 0 && currentUser?.id) {
      const documentIds = documentAttachments.map(a => a.id);
      try {
        await markDocumentsAsDeleted(currentUser.id, documentIds);
        console.log(`[ChatPage] Marked ${documentIds.length} document(s) as deleted when clearing attachments`);
      } catch (error) {
        console.error('[ChatPage] Failed to mark documents as deleted:', error);
        // Don't block the operation if Firestore update fails
        // The documents will be cleaned up by backend eventually
      }
    }
    
    // Clear attachments and pending files from state
    setAttachments([]);
    setPendingFiles([]);
  }, [attachments, currentUser]);

  const handleNewChat = useCallback(() => {
    dispatch(clearChat(false));
    const newConversationId = uuidv4(); // Generate new conversation ID
    dispatch(setCurrentConversation(newConversationId));
    setSelectedSession(null);
    setMessageInput('');
    
    // Clear attachments and mark as deleted in Firestore
    clearAttachmentsAndMarkDeleted();
    
    // Generate new message ID for new conversation
    const newMessageId = uuidv4();
    setCurrentMessageId(newMessageId);
    console.log(`[ChatPage] New chat - generated message ID: ${newMessageId}`);

    // For new chat, set default thinking mode for first message
    const defaultMode = getDefaultMode(undefined, true);
    setCurrentThinkingMode(defaultMode);
    
    // Reset progress and busy state
    setProgress(''); 
    setCurrentConversationBusy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (isHistoryOpenMobile && event.target instanceof Element) {
      const historyPanel = document.querySelector(`.${styles.historyPanel}`);
      if (historyPanel && !historyPanel.contains(event.target)) {
        setIsHistoryOpenMobile(false);
      }
    }
  }, [isHistoryOpenMobile]);

  // Unified thinking mode setter with validation
  const setThinkingMode = useCallback((mode: ThinkingMode) => {
    // Get current conversation to check availability
    const currentConversation = conversations.find(c => c.id === currentConversationId);
    const availableModes = getAvailableModes(currentConversation?.thinkingType, isFirstMessage);
    
    // Only set the mode if it's available for current context
    if (availableModes.includes(mode)) {
      setCurrentThinkingMode(mode);
    }
  }, [conversations, currentConversationId, isFirstMessage]);


  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  // Add this useEffect to run handleNewChat() once on mount.
  useEffect(() => {
    handleNewChat();
  }, [handleNewChat]);

  // Add the handleDeleteConversation function
  const handleDeleteConversation = async (conversationId: string, thinkingType: 'reflection' | 'fraktal' | 'prompt-graph') => {
    try {
      // Check authentication state first
      if (!currentUser?.id || !isAuthenticated) {
        console.error('User not authenticated');
        return;
      }
      
      // Get a fresh token for the deletion operation
      const validToken = await dispatch(getValidToken()).unwrap();
      
      // If we're deleting the current conversation, deselect it first
      if (currentConversationId === conversationId) {
        // Set selected session to null
        setSelectedSession(null);
      }
      
      await dispatch(
        deleteConversationThunk({
          userToken: validToken,
          conversationId: conversationId,
          type: thinkingType
        })
      );
      
      // If we deleted the current conversation, start a new chat
      if (currentConversationId === conversationId) {
        handleNewChat();
      }
      
      // Show notification or toast here if desired
    } catch (error) {
      console.error('Error deleting conversation:', error);
      // Handle error - show error notification if desired
      // Could add user notification here in future
    }
  };

  return (
    <Box className={styles.chatPageContainer}>
      {/* Chat Window Container First */}
      <Box className={styles.chatWindowContainer}>
        <ChatHeader toggleHistoryMobile={toggleHistoryMobile} handleNewChat={handleNewChat} />

        {/* Chat Messages */}
        <Box ref={chatWindowRef} className={styles.chatWindow}>
          {loadingMessages ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          ) : (
            <ChatMessages
              messages={messages}
              user={user}
              conversationId={currentConversationId || ''}
            />
          )}
        </Box>

        {/* Chat Input */}
        <ChatInput
          key={currentConversationId}
          messageInput={messageInput}
          setMessageInput={setMessageInput}
          handleKeyDown={handleKeyDown}
          loading={loading}
          conversationId={currentConversationId || ''}
          currentThinkingMode={currentThinkingMode}
          onThinkingModeChange={setThinkingMode}
          isFirstMessage={isFirstMessage}
          handleSendMessage={handleSendMessage}
          onStartNewChat={handleNewChat}
          currentMessageId={currentMessageId}
          attachments={attachments}
          onAttachmentsChange={setAttachments}
          onPendingFilesChange={setPendingFiles}
        />
      </Box>

      {/* History Panel - only render if we're initialized */}
      {appInitialized && (
        <History
          isHistoryOpenMobile={isHistoryOpenMobile}
          handleNewChat={handleNewChat}
          conversations={conversations}
          selectedSession={selectedSession}
          handleSelectSession={handleSelectSession}
          setIsHistoryOpenMobile={setIsHistoryOpenMobile}
          handleDeleteConversation={handleDeleteConversation}
          loadingHistory={loadingHistory}
        />
      )}

      {/* Overlay for Mobile */}
      <Box
        className={`${styles.overlay} ${isHistoryOpenMobile ? styles.open : ''}`}
        onClick={() => setIsHistoryOpenMobile(false)}
      />
    </Box>
  );
};

export default ChatPage;
